import React, { useEffect, useRef, useState } from 'react';
import { Bot, User } from 'lucide-react';
import { logEvent } from '../lib/amplitude';
import SynthTransport from './SynthTransport';

// Real MidiMate outputs for these prompts, generated in Pro mode. The files live in public/demos.
const demos = [
  {
    id: "phonk",
    label: "Drift phonk",
    prompt: "Dark drift phonk beat with a cowbell melody and heavy 808 bass",
    reply: "An 8-bar dark drift phonk beat at 148 BPM with an F-sharp Phrygian edge, heavy 808-style bass, rolling hats, and a cowbell-style melody approximated with a MIDI square lead.",
    tracks: ["Cowbell Lead", "808 Bass", "Dark Stabs", "Drums"]
  },
  {
    id: "afro-house",
    label: "Afro house",
    prompt: "Afro house groove in A minor with percussion, a deep bassline and warm Rhodes chords",
    reply: "An 8-bar Afro house groove in A minor at 122 BPM, with interlocking percussion, a deep syncopated bassline, and warm Rhodes ninth chords.",
    tracks: ["Rhodes", "Deep Bass", "Afro Percussion"]
  },
  {
    id: "pop-country",
    label: "Pop country",
    prompt: "Upbeat pop country song with acoustic guitar, bass and drums",
    reply: "An upbeat eight-bar pop-country groove in D major at 116 BPM, with bright acoustic strums, a bouncy bassline, and punchy backbeat drums.",
    tracks: ["Acoustic Guitar", "Bass", "Drums"]
  },
  {
    id: "lofi",
    label: "Lo-fi piano",
    prompt: "Emotional lo-fi piano chords in D minor with a simple melody",
    reply: "An intimate 8-bar lo-fi piano piece in D minor at 76 BPM, with warm extended chords and a simple, wistful melody.",
    tracks: ["Piano Chords", "Piano Melody"]
  }
];

function LandingDemo() {
  const [activeId, setActiveId] = useState(demos[0].id);
  const [buffer, setBuffer] = useState(null);
  const visualizerRef = useRef(null);
  const demo = demos.find(d => d.id === activeId);

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.PUBLIC_URL}/demos/${activeId}.mid`;
    setBuffer(null);
    fetch(url)
      .then((r) => r.arrayBuffer())
      .then((ab) => {
        if (cancelled) return;
        setBuffer(ab);
        if (visualizerRef.current) visualizerRef.current.src = url;
      })
      .catch((e) => console.error('Could not load demo MIDI', e));
    return () => { cancelled = true; };
  }, [activeId]);

  const handleSelect = (id) => {
    logEvent('Landing Demo Selected', { demo: id });
    setActiveId(id);
  };

  return (
    <div className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-2xl p-4 sm:p-6 text-left">
      <p className="text-sm font-medium text-gray-500 mb-3">Hear what MidiMate makes from one sentence:</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {demos.map(d => (
          <button
            key={d.id}
            type="button"
            onClick={() => handleSelect(d.id)}
            aria-pressed={d.id === activeId}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              d.id === activeId
                ? 'bg-[#f05477] border-[#f05477] text-white'
                : 'bg-white border-[#f05477] text-[#f05477] hover:bg-[#f05477]/10'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div className="flex justify-end mb-3">
        <div className="flex items-start space-x-3 flex-row-reverse space-x-reverse max-w-[85%]">
          <div className="flex-shrink-0 rounded-full p-2 bg-primary text-primary-foreground">
            <User className="h-5 w-5" />
          </div>
          <div className="rounded-lg p-3 bg-primary text-primary-foreground text-sm">{demo.prompt}</div>
        </div>
      </div>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 rounded-full p-2 bg-muted">
          <Bot className="h-5 w-5" />
        </div>
        <div className="rounded-lg p-3 bg-gray-100 min-w-0 flex-grow">
          <p className="text-sm mb-2">{demo.reply}</p>
          <div className="midi-player-container space-y-2 bg-white rounded-md border p-2">
            <SynthTransport
              buffer={buffer}
              visualizerRef={visualizerRef}
              onStart={() => logEvent('Landing Demo Played', { demo: activeId })}
            />
            <div className="overflow-y-auto max-w-full" style={{ maxHeight: '220px' }}>
              <midi-visualizer
                ref={visualizerRef}
                type="piano-roll"
                style={{ minWidth: '0', width: '100%', fontSize: '8px', overflowX: 'hidden' }}
              ></midi-visualizer>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">Tracks: {demo.tracks.join(', ')}. Sign up to make your own and download the MIDI.</p>
        </div>
      </div>
    </div>
  );
}

export default LandingDemo;
